import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { environment } from "environment/environment";
import MerchantEmptyState, { EmptyKind } from "./MerchantEmptyState";
import MerchantLoader from "./MerchantLoader";
import { isLiveMerchantPlatform } from "./merchantBranding";
import { formatFullAddress } from "helpers/orderAddress";

export interface MerchantOrder {
  zuluOrderId?: string | null;
  zuluUniqueId?: string | null;
  shopifyOrderNumber?: string | null;
  shopifyOrderId?: string | null;
  customerName?: string | null;
  deliveryTime?: string | null;
  createdAt?: string;
  deliveryPostal?: string | null;
  deliveryCity?: string | null;
  orderStatus?: string | null;
  shopifyFulfillmentStatus?: string | null;
  paymentStatus?: string | null;
  shopifyFinancialStatus?: string | null;
  deliveryMethod?: string | null;
  driverName?: string | null;
  totalFare?: number | null;
}

export type OrderTone = "received" | "assigned" | "picked" | "delivered" | "other";

export type OrderListFilter = "all" | "received" | "assigned" | "picked" | "delivered" | "inTransit";

/** Dashboard / orders list date window sent as `range` (+ optional from/to). */
export type DashboardDatePreset = "today" | "week" | "month" | "custom";

export interface DashboardDateFilter {
  preset: DashboardDatePreset;
  /** Selected calendar day when preset is `custom` (also used for picker highlight). */
  day: Date;
}

export interface MerchantLabelContext {
  platform: string;
  shop: string;
  session: string;
  format?: string;
}

interface MerchantOrdersPanelProps {
  platform: string;
  shop: string;
  session: string;
  dateFilter: DashboardDateFilter;
  statusFilter: OrderListFilter;
  onStatusFilter: (next: OrderListFilter) => void;
  onResetDateFilter?: () => void;
  labelFormat?: string;
  refreshKey?: number;
  onViewOrder?: (order: MerchantOrder) => void;
}

const PAGE_SIZE = 25;

export const MerchantOrdersPanel: React.FC<MerchantOrdersPanelProps> = ({
  platform,
  shop,
  session,
  dateFilter,
  statusFilter,
  onStatusFilter,
  onResetDateFilter,
  labelFormat,
  refreshKey = 0,
  onViewOrder,
}) => {
  const [orders, setOrders] = useState<MerchantOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState<MerchantOrder | null>(null);
  const ordersRef = useRef<MerchantOrder[]>([]);
  const readyRef = useRef(false);
  const requestId = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const loadOrders = useCallback(async (options?: { silent?: boolean }) => {
    if (!session || !isLiveMerchantPlatform(platform)) {
      setLoading(false);
      return;
    }
    const silent = Boolean(options?.silent) && readyRef.current;
    const nextRequest = requestId.current + 1;
    requestId.current = nextRequest;
    const params = new URLSearchParams({
      session,
      status: statusFilter,
    });
    appendDashboardRangeParams(params, dateFilter);
    if (shop) {
      params.set("shop", shop);
    }
    if (search) {
      params.set("q", search);
    }
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/${platform}/orders?${params.toString()}`
      );
      const body = await response.json();
      if (nextRequest !== requestId.current) {
        return;
      }
      if (!response.ok || body.success === false) {
        if (silent) {
          return;
        }
        setError(body.message || "Could not load orders.");
        setOrders([]);
        ordersRef.current = [];
        setTotal(0);
        return;
      }
      const rows = Array.isArray(body.data && body.data.orders) ? body.data.orders : [];
      ordersRef.current = rows;
      readyRef.current = true;
      setOrders(rows);
      setTotal(typeof body.data?.total === "number" ? body.data.total : rows.length);
      if (!silent) {
        setPage(1);
      }
      setError("");
    } catch {
      if (nextRequest !== requestId.current || silent) {
        return;
      }
      setError("Could not load orders. Try again.");
      setOrders([]);
      ordersRef.current = [];
      setTotal(0);
    } finally {
      if (nextRequest === requestId.current) {
        setLoading(false);
      }
    }
  }, [session, shop, platform, dateFilter, statusFilter, search]);

  const loadOrdersRef = useRef(loadOrders);
  loadOrdersRef.current = loadOrders;

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!refreshKey) {
      return;
    }
    loadOrdersRef.current({ silent: true });
  }, [refreshKey]);

  const pageCount = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  const visible = useMemo(
    () => orders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [orders, page]
  );

  return (
    <section className="zulu-plugin__stage-body">
      <div className="zulu-plugin__orders-toolbar">
        <div className="zulu-plugin__status-chips">
          {STATUS_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`zulu-plugin__filter-chip${statusFilter === item.id ? " on" : ""}`}
              onClick={() => onStatusFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <label className="zulu-plugin__search">
          <span className="sr-only">Search orders</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search order # or customer"
          />
        </label>
      </div>
      <div className="zulu-plugin__orders">
        <div className="zulu-plugin__orders-head">
          <div>
            <h2>All orders</h2>
            <p>
              {loading
                ? "Loading live Shopify and Zulu deliveries…"
                : `${total} ${total === 1 ? "order" : "orders"}`}
              {` · ${formatDashboardDateFilterLabel(dateFilter).replace(/^For /, "")}`}
            </p>
          </div>
          <button
            type="button"
            className="zulu-plugin__refresh-btn"
            onClick={() => loadOrders()}
            disabled={loading}
            aria-busy={loading}
            aria-label="Refresh orders"
          >
            <IconRefresh />
            <span>{loading ? "Refreshing…" : "Refresh"}</span>
          </button>
        </div>
        {error && orders.length ? <p className="zulu-plugin__orders-error">{error}</p> : null}
        {error && !orders.length && !loading ? (
          <MerchantEmptyState kind="error" actionLabel="Try again" onAction={() => loadOrders()} />
        ) : loading && !orders.length ? (
          <MerchantLoader size="panel" label="Loading live deliveries…" />
        ) : (
          <>
            {loading ? <MerchantLoader size="inline" label="Updating deliveries…" /> : null}
            <OrdersTable
              orders={visible}
              loading={loading}
              emptyKind={
                search || statusFilter !== "all" || !isDefaultDateFilter(dateFilter)
                  ? "filters"
                  : "orders"
              }
              emptyActionLabel={
                search || statusFilter !== "all" || !isDefaultDateFilter(dateFilter)
                  ? "Clear filters"
                  : undefined
              }
              onEmptyAction={
                search || statusFilter !== "all" || !isDefaultDateFilter(dateFilter)
                  ? () => {
                      setQuery("");
                      setSearch("");
                      onStatusFilter("all");
                      if (onResetDateFilter) {
                        onResetDateFilter();
                      }
                    }
                  : undefined
              }
              onView={onViewOrder || setViewing}
              labelContext={{ platform, shop, session, format: labelFormat }}
            />
          </>
        )}
        {orders.length > PAGE_SIZE ? (
          <div className="zulu-plugin__pager">
            <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {pageCount}
            </span>
            <button type="button" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>
              Next
            </button>
          </div>
        ) : null}
      </div>
      {onViewOrder ? null : (
        <OrderSheet
          order={viewing}
          onClose={() => setViewing(null)}
          labelContext={{ platform, shop, session, format: labelFormat }}
        />
      )}
    </section>
  );
};

export const OrdersTable: React.FC<{
  orders: MerchantOrder[];
  loading?: boolean;
  emptyKind?: EmptyKind;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  onView: (order: MerchantOrder) => void;
  labelContext?: MerchantLabelContext;
}> = ({ orders, loading, emptyKind = "orders", emptyActionLabel, onEmptyAction, onView, labelContext }) => {
  if (!orders.length) {
    if (loading) {
      return null;
    }
    return (
      <MerchantEmptyState
        kind={emptyKind}
        actionLabel={emptyActionLabel}
        onAction={onEmptyAction}
      />
    );
  }
  return (
    <div className={`zulu-plugin__table-wrap${loading ? " is-loading" : ""}`}>
      <table className="zulu-plugin__table">
        <thead>
          <tr>
            <th>Zulu order #</th>
            <th>Shopify order #</th>
            <th>Customer</th>
            <th>Delivery date & time</th>
            <th>Postal code</th>
            <th>Status</th>
            <th>Proof</th>
            <th>View</th>
            <th>Label</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => {
            const tone = orderTone(order.orderStatus);
            return (
              <tr key={`${order.zuluUniqueId || order.shopifyOrderId || index}`}>
                <td>{order.zuluUniqueId || "—"}</td>
                <td>{formatShopifyNumber(order.shopifyOrderNumber || order.shopifyOrderId)}</td>
                <td>{order.customerName || "—"}</td>
                <td>{formatOrderDateTime(order.deliveryTime || order.createdAt)}</td>
                <td>{formatPostal(order.deliveryPostal)}</td>
                <td>
                  <span className={`zulu-plugin__pill ${tone}`}>{orderStatusLabel(order)}</span>
                </td>
                <td>
                  {tone === "delivered" ? (
                    <button
                      type="button"
                      className="zulu-plugin__proof-link"
                      onClick={() => onView(order)}
                    >
                      View proof
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="zulu-plugin__view"
                    aria-label={`View order ${order.zuluUniqueId || order.shopifyOrderNumber || ""}`}
                    onClick={() => onView(order)}
                  >
                    <IconEye />
                  </button>
                </td>
                <td>
                  <ShippingLabelButton order={order} labelContext={labelContext} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

interface OrderAddress {
  street?: string | null;
  unit?: string | null;
  city?: string | null;
  postalCode?: string | null;
  companyName?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  lat?: number | null;
  lng?: number | null;
}

interface MerchantOrderDetails extends MerchantOrder {
  deliveryOption?: string | null;
  settingsType?: string | null;
  deliveryServiceName?: string | null;
  deliveryPin?: string | null;
  packageReadyTime?: string | null;
  completedTime?: string | null;
  totalItems?: string | null;
  totalWeight?: string | null;
  totalDistance?: string | null;
  vehicleType?: string | null;
  subTotal?: number | null;
  hst?: number | null;
  platformTax?: number | null;
  platformTaxLabel?: string | null;
  platformTaxLines?: Array<{
    title: string;
    rate?: number | null;
    price: number;
  }> | null;
  notes?: string | null;
  sizeDescription?: string | null;
  description?: string | null;
  sentBy?: string | null;
  transmissionStatus?: string | null;
  pickup?: OrderAddress | null;
  delivery?: OrderAddress | null;
  statusDetails?: {
    status?: string | null;
    notes?: string | null;
    receiverName?: string | null;
    date?: string | null;
    time?: string | null;
    pickupName?: string | null;
    deliveredName?: string | null;
    deliveredDate?: string | null;
    deliveredTime?: string | null;
  } | null;
  proofOfDelivery?: {
    recipientName?: string | null;
    deliveredDate?: string | null;
    deliveredTime?: string | null;
    deliveredAt?: string | null;
    signature?: string | null;
    photo?: string | null;
    notes?: string | null;
  } | null;
}

const orderLookupId = (order?: MerchantOrder | null): string =>
  String(order?.zuluOrderId || order?.zuluUniqueId || order?.shopifyOrderId || "").trim();

export const OrderSheet: React.FC<{
  order: MerchantOrder | null;
  onClose: () => void;
  labelContext?: MerchantLabelContext;
}> = ({ order, onClose, labelContext }) => {
  const [details, setDetails] = useState<MerchantOrderDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadedKey = useRef("");
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const lookupId = orderLookupId(order);
  const session = labelContext?.session || "";
  const shop = labelContext?.shop || "";
  const platform = labelContext?.platform || "";

  useEffect(() => {
    if (!order) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [order]);

  useEffect(() => {
    if (!order || !platform || !session || !lookupId) {
      if (!order) {
        setDetails(null);
        setError("");
        setLoading(false);
        loadedKey.current = "";
      }
      return;
    }
    if (loadedKey.current === lookupId) {
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ session });
    if (shop) {
      params.set("shop", shop);
    }
    setLoading(true);
    setError("");
    setDetails(null);
    fetch(
      `${environment.api_url}/api/v1/integrations/${platform}/orders/${encodeURIComponent(
        lookupId
      )}/details?${params.toString()}`
    )
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok || body.success === false) {
          throw new Error(body.message || "Could not load order details.");
        }
        return body.data as MerchantOrderDetails;
      })
      .then((data) => {
        if (!cancelled) {
          loadedKey.current = lookupId;
          setDetails(data);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          loadedKey.current = lookupId;
          setError(err.message || "Could not load order details.");
          setDetails(order);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lookupId, order, platform, session, shop]);

  if (!order) {
    return null;
  }
  const view: MerchantOrderDetails = details || order;
  const tone = orderTone(view.orderStatus);
  const labelOrder: MerchantOrder = {
    ...order,
    ...view,
    zuluOrderId: view.zuluOrderId || order.zuluOrderId,
  };

  return (
    <div className="zulu-plugin__sheet" onClick={onClose}>
      <div
        className="zulu-plugin__sheet-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="zulu-order-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="zulu-plugin__sheet-head">
          <div>
            <p>Order details</p>
            <h3 id="zulu-order-dialog-title">
              {view.zuluUniqueId || formatShopifyNumber(view.shopifyOrderNumber)}
            </h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {loading && !details ? (
          <MerchantLoader size="inline" label="Loading order details…" />
        ) : null}
        {error ? <p className="zulu-plugin__orders-error">{error}</p> : null}
        {!loading || details ? (
        <div className="zulu-plugin__detail-stack">
          <section>
            <h4>Order information</h4>
            <DetailRow label="Zulu order #" value={view.zuluUniqueId} />
            <DetailRow label="Shopify order #" value={formatShopifyNumber(view.shopifyOrderNumber || view.shopifyOrderId)} />
            <DetailRow
              label="Status"
              value={<span className={`zulu-plugin__pill ${tone}`}>{orderStatusLabel(view)}</span>}
            />
            <DetailRow label="Customer" value={view.customerName || order.customerName} />
            <DetailRow
              label="Delivery service"
              value={
                checkoutServiceName(view.deliveryServiceName) ||
                checkoutServiceName(order.deliveryMethod) ||
                serviceLabel(view.settingsType)
              }
            />
            <DetailRow label="Delivery method" value={deliveryOptionLabel(view.deliveryOption, view.deliveryPin)} />
            <DetailRow label="Driver" value={assignedDriverName(view.driverName)} />
            <DetailRow label="Weight" value={view.totalWeight} />
            <DetailRow
              label="Delivery address"
              value={
                formatFullAddress(view.delivery, {
                  city: order.deliveryCity,
                  postalCode: order.deliveryPostal,
                }) || null
              }
            />
          </section>
          <ProofOfDeliverySection
            proof={view.proofOfDelivery}
            fallbackName={view.statusDetails?.deliveredName || view.statusDetails?.receiverName}
            fallbackDate={
              view.statusDetails?.deliveredDate || view.statusDetails?.date
                ? `${view.statusDetails?.deliveredDate || view.statusDetails?.date} ${view.statusDetails?.deliveredTime || view.statusDetails?.time || ""}`.trim()
                : null
            }
            fallbackNotes={view.statusDetails?.notes}
          />
          <section>
            <h4>Pickup</h4>
            <AddressBlock address={view.pickup} fallbackCity={null} fallbackPostal={null} />
          </section>
          <section>
            <h4>Quote</h4>
            <DetailRow label="Distance" value={view.totalDistance} />
            <DetailRow label="Subtotal" value={formatMoney(view.subTotal)} />
            <DetailRow label="HST" value={formatMoney(view.hst)} />
            <DetailRow label="Total fare" value={formatMoney(view.totalFare)} />
          </section>
        </div>
        ) : null}
        <div className="zulu-plugin__sheet-actions">
          <ShippingLabelButton order={labelOrder} labelContext={labelContext} wide />
        </div>
      </div>
    </div>
  );
};

const ProofOfDeliverySection: React.FC<{
  proof?: MerchantOrderDetails["proofOfDelivery"];
  fallbackName?: string | null;
  fallbackDate?: string | null;
  fallbackNotes?: string | null;
}> = ({ proof, fallbackName, fallbackDate, fallbackNotes }) => {
  const recipientName = proof?.recipientName || fallbackName;
  const deliveredAt = proof?.deliveredAt || fallbackDate;
  const signature = proof?.signature;
  const photos = [proof?.photo].filter((item): item is string => Boolean(item));
  const driverNotes = String(proof?.notes || fallbackNotes || "").trim();
  const hasProof = Boolean(recipientName || deliveredAt || signature || photos.length || driverNotes);

  return (
    <section className="zulu-plugin__pod">
      <h4>Proof of delivery</h4>
      {hasProof ? (
        <>
          <DetailRow label="Delivery date & time" value={deliveredAt} />
          <DetailRow label="Recipient name" value={recipientName} />
          {driverNotes ? (
            <DetailRow
              label="Notes"
              value={<span style={{ whiteSpace: "pre-line" }}>{driverNotes}</span>}
            />
          ) : null}
          {signature || photos.length ? (
            <div className="zulu-plugin__pod-media">
              {signature ? (
                <div>
                  <span>Recipient signature</span>
                  <div className="zulu-plugin__pod-frame is-signature">
                    <img src={signature} alt="Recipient signature" />
                  </div>
                </div>
              ) : null}
              {photos.map((photo, index) => (
                <div key={`${index}-${photo.slice(0, 24)}`}>
                  <span>{photos.length > 1 ? `Delivery photo ${index + 1}` : "Delivery photo"}</span>
                  <div className="zulu-plugin__pod-frame">
                    <img src={photo} alt="Delivery photo" />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <p className="zulu-plugin__pod-empty">
          Proof of delivery will appear here after the driver completes this order.
        </p>
      )}
    </section>
  );
};

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  const empty = value == null || value === "" || value === "—";
  return (
    <div className="zulu-plugin__detail-row">
      <span>{label}</span>
      <strong>{empty ? "—" : value}</strong>
    </div>
  );
};

function assignedDriverName(value?: string | null): string | null {
  const name = String(value || "").trim();
  if (!name || /^unknown driver$/i.test(name) || /^no driver assigned$/i.test(name)) {
    return null;
  }
  return name;
}

const AddressBlock: React.FC<{
  address?: OrderAddress | null;
  fallbackCity?: string | null;
  fallbackPostal?: string | null;
  fallbackName?: string | null;
}> = ({ address, fallbackCity, fallbackPostal, fallbackName }) => {
  const city = (address && address.city) || fallbackCity;
  const postal = formatPostal((address && address.postalCode) || fallbackPostal);
  const street = [address && address.unit, address && address.street].filter(Boolean).join(" ");
  return (
    <>
      <DetailRow label="Address" value={street || null} />
      <DetailRow label="City" value={city} />
      <DetailRow label="Postal code" value={postal === "—" ? null : postal} />
      <DetailRow label="Company" value={address && address.companyName} />
      <DetailRow label="Contact" value={(address && address.contactName) || fallbackName} />
      <DetailRow label="Phone" value={address && address.contactPhone} />
    </>
  );
};

const ShippingLabelButton: React.FC<{
  order: MerchantOrder;
  labelContext?: MerchantLabelContext;
  wide?: boolean;
}> = ({ order, labelContext, wide }) => {
  const [busy, setBusy] = useState(false);
  const orderId = order.zuluOrderId;
  if (!labelContext || !orderId) {
    return (
      <button
        type="button"
        className={`zulu-plugin__label${wide ? " is-wide" : ""}`}
        disabled
        title="Shipping label is available after Zulu imports this order"
      >
        <IconLabel />
        {wide ? "Shipping label" : null}
      </button>
    );
  }
  return (
    <button
      type="button"
      className={`zulu-plugin__label${wide ? " is-wide" : ""}${busy ? " is-busy" : ""}`}
      disabled={busy}
      aria-label={`Download shipping label for ${order.zuluUniqueId || order.shopifyOrderNumber || "order"}`}
      onClick={() => {
        setBusy(true);
        downloadMerchantShippingLabel(orderId, labelContext)
          .catch(() => undefined)
          .finally(() => setBusy(false));
      }}
    >
      <IconLabel />
      {wide ? (busy ? "Preparing label…" : "Shipping label") : null}
    </button>
  );
};

export async function downloadMerchantShippingLabel(
  orderId: string,
  context: MerchantLabelContext
): Promise<void> {
  const params = new URLSearchParams({ session: context.session });
  if (context.shop) {
    params.set("shop", context.shop);
  }
  if (context.format) {
    params.set("format", context.format);
  }
  const response = await fetch(
    `${environment.api_url}/api/v1/integrations/${context.platform}/orders/${orderId}/shipping-label?${params.toString()}`
  );
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok || contentType.indexOf("application/pdf") === -1) {
    window.alert("Failed to generate PDF. Please try again later.");
    return;
  }
  const blob = await response.blob();
  const pdfURL = URL.createObjectURL(blob);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const newWindow = window.open(pdfURL, "_blank");
  if (!newWindow && !isSafari) {
    window.alert("Pop-up blocked! Please enable pop-ups for this site.");
  }
  window.setTimeout(() => URL.revokeObjectURL(pdfURL), 60000);
}

const STATUS_FILTERS: Array<{ id: OrderListFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "received", label: "Received" },
  { id: "assigned", label: "Assigned" },
  { id: "picked", label: "Picked" },
  { id: "delivered", label: "Delivered" },
];

export const formatOrderDateTime = (value?: string | null): string => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const day = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${day} / ${time}`;
};

export const formatShopifyNumber = (value?: string | null): string => {
  if (!value) {
    return "—";
  }
  const text = String(value).trim();
  return text.startsWith("#") ? text : `#${text}`;
};

export const formatPostal = (value?: string | null): string => {
  const compact = String(value || "").replace(/\s+/g, "").toUpperCase();
  if (!compact) {
    return "—";
  }
  if (compact.length === 6) {
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
  }
  return compact;
};

const DELIVERED_STATUSES = [
  "delivered",
  "completed",
  "dropped",
  "redelivered",
  "billed",
  "delivered_today",
  "reattempted_and_delivered",
];
const PICKED_STATUSES = ["picked", "current"];
const ASSIGNED_STATUSES = ["assigned", "accepted"];

export const orderTone = (status?: string | null): OrderTone => {
  const value = String(status || "").toLowerCase();
  if (DELIVERED_STATUSES.includes(value)) {
    return "delivered";
  }
  if (PICKED_STATUSES.includes(value)) {
    return "picked";
  }
  if (ASSIGNED_STATUSES.includes(value)) {
    return "assigned";
  }
  if (
    !value ||
    ["not_assigned", "future", "pushed", "pending", "pending_payment"].includes(value)
  ) {
    return "received";
  }
  return "other";
};

const orderStatusLabel = (order: {
  orderStatus?: string | null;
}): string => {
  const tone = orderTone(order.orderStatus);
  if (tone === "other") {
    return prettyStatus(order.orderStatus) || "Needs attention";
  }
  return statusLabel(tone);
};

export const statusLabel = (tone?: string): string => {
  if (tone === "delivered") {
    return "Delivered";
  }
  if (tone === "picked") {
    return "Picked";
  }
  if (tone === "assigned") {
    return "Assigned";
  }
  if (tone === "received") {
    return "Received";
  }
  return "Needs attention";
};

const prettyStatus = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const deliveryOptionLabel = (option?: string | null, pin?: string | null): string => {
  const label = prettyStatus(option);
  if (!label) {
    return "";
  }
  if (option === "delivery_verification_pin" && pin) {
    return `${label} (${pin})`;
  }
  return label;
};

const HANDOFF_LABELS = [
  "drop at door",
  "drop at mailbox",
  "drop at front desk",
  "call me to confirm",
  "signature required",
  "return the package",
  "reattempt tomorrow",
];

const checkoutServiceName = (value?: string | null): string => {
  const title = String(value || "").replace(/\s+/g, " ").trim();
  if (!title) {
    return "";
  }
  const parts = title.split(/\s+[—-]\s+/);
  if (parts.length < 2) {
    return title;
  }
  const suffix = parts.slice(1).join(" — ").toLowerCase();
  return HANDOFF_LABELS.indexOf(suffix) === -1 ? title : parts[0].trim();
};

const serviceLabel = (settingsType?: string | null): string => {
  switch (String(settingsType || "").toUpperCase()) {
    case "ECONOMY":
      return "Zulu Economy";
    case "PRIORITY":
      return "Zulu Priority";
    case "RUSH":
      return "Zulu Rush";
    case "AFTER_HOURS":
      return "Zulu After Hours";
    case "STANDARD":
      return "Zulu Standard";
    default:
      return prettyStatus(settingsType);
  }
};

const formatWindow = (from?: string | null, to?: string | null): string => {
  if (!from && !to) {
    return "";
  }
  if (from && to) {
    return `${formatOrderDateTime(from)} to ${formatOrderDateTime(to)}`;
  }
  return formatOrderDateTime(from || to);
};

const formatMoney = (value?: number | null): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "";
  }
  return `$${value.toFixed(2)}`;
};

export const isoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const startOfLocalDay = (date: Date): Date => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

/** Default dashboard filter: single day = today (shown as Pick a date). */
export const createDefaultDateFilter = (): DashboardDateFilter => ({
  preset: "custom",
  day: startOfLocalDay(new Date()),
});

/** @deprecated Use createDefaultDateFilter */
export const createTodayDateFilter = createDefaultDateFilter;

export const isDefaultDateFilter = (filter: DashboardDateFilter): boolean =>
  filter.preset === "custom" &&
  isoDate(filter.day || new Date()) === isoDate(new Date());

/** Append `range` / `from` / `to` query params for merchant dashboard APIs. */
export const appendDashboardRangeParams = (
  params: URLSearchParams,
  filter: DashboardDateFilter
): void => {
  const preset = filter.preset || "custom";
  params.set("range", preset);
  if (preset === "custom") {
    const day = isoDate(filter.day || new Date());
    params.set("from", day);
    params.set("to", day);
  }
};

export const formatDashboardDateFilterLabel = (
  filter: DashboardDateFilter
): string => {
  switch (filter.preset) {
    case "week":
      return "For this week";
    case "month":
      return "For this month";
    case "custom":
      return `For ${filter.day.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`;
    case "today":
      return "For today";
    default:
      return `For ${filter.day.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`;
  }
};

export const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <polyline points="21 3 21 9 15 9" />
  </svg>
);

const IconEye = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconLabel = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5" />
    <path d="M10 13h6M10 17h6" />
  </svg>
);
